import { getQueuedInspections, removeQueuedInspection } from './offlineDB';
import { submitInspection } from './api';
import { computeCheckUpdates } from './photoCheckMapper';
import { analyzePhoto, validateVehicleConsistency } from './aiAnalysis';
import { authFetch } from '@/utils/authFetch';
import type { InspectionType } from '@/types';

let syncing = false;

async function uploadAndAnalyzePhoto(vp: any, categories: any[], inspectionType: InspectionType): Promise<{
  updatedVp: any;
  checkUpdates: { categoryId: string; checkId: string; status: 'pass' | 'fail'; aiReasoning?: string }[];
}> {
  const file = vp.photo.pendingFile as File;

  const formData = new FormData();
  formData.append('photo', file);
  formData.append('checkId', vp.promptId);

  const uploadRes = await authFetch('/api/inspections/temp/photos', {
    method: 'POST',
    body: formData,
  });

  if (!uploadRes.ok) throw new Error('Photo upload failed');
  const { url } = await uploadRes.json();

  const validation = await validateVehicleConsistency(file, undefined);
  const analysis = await analyzePhoto(file, vp.promptId);

  const updatedPhoto = {
    ...vp.photo,
    uri: url,
    pendingUpload: false,
    pendingFile: undefined,
    aiAnalysis: analysis,
    vehicleValidation: validation,
    isInvalid: !validation.isSameVehicle && validation.confidence > 0.85,
  };

  const checkUpdates = updatedPhoto.isInvalid
    ? []
    : computeCheckUpdates(vp.promptId, analysis, categories, inspectionType);

  return {
    updatedVp: { ...vp, photo: updatedPhoto },
    checkUpdates,
  };
}

export async function processPendingPhotos(inspection: any): Promise<any> {
  if (!inspection.vehiclePhotos?.some((vp: any) => vp.photo?.pendingUpload)) {
    return inspection;
  }

  let updatedCategories = JSON.parse(JSON.stringify(inspection.categories));
  const updatedVehiclePhotos = [...inspection.vehiclePhotos];

  for (let i = 0; i < updatedVehiclePhotos.length; i++) {
    const vp = updatedVehiclePhotos[i];
    if (!vp.photo?.pendingUpload || !vp.photo?.pendingFile) continue;

    try {
      const { updatedVp, checkUpdates } = await uploadAndAnalyzePhoto(vp, updatedCategories, inspection.type);
      updatedVehiclePhotos[i] = updatedVp;

      for (const { categoryId, checkId, status, aiReasoning } of checkUpdates) {
        const cat = updatedCategories.find((c: any) => c.id === categoryId);
        if (cat) {
          const check = cat.checks.find((ch: any) => ch.id === checkId);
          if (check && (check.status === null || (status === 'fail' && check.status === 'pass') || (status === 'pass' && check.status === 'fail'))) {
            check.status = status;
            if (aiReasoning) check.aiReasoning = aiReasoning;
          }
        }
      }
    } catch (err) {
      console.error(`Failed to process pending photo for ${vp.promptId}:`, err);
    }
  }

  return {
    ...inspection,
    categories: updatedCategories,
    vehiclePhotos: updatedVehiclePhotos,
  };
}

export async function syncQueuedInspections(): Promise<{ synced: number; failed: number }> {
  if (syncing) return { synced: 0, failed: 0 };
  syncing = true;

  let synced = 0;
  let failed = 0;

  try {
    const queued = await getQueuedInspections();

    for (const item of queued) {
      try {
        console.log(`Processing inspection ${item.id}...`);
        const processedInspection = await processPendingPhotos(item.inspection);
        await submitInspection(processedInspection);
        await removeQueuedInspection(item.id);
        synced++;
        console.log(`Synced inspection ${item.id}`);
      } catch (err) {
        console.error(`Failed to sync inspection ${item.id}:`, err);
        failed++;
      }
    }
  } finally {
    syncing = false;
  }

  return { synced, failed };
}